import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import {
  GridPreferencePanelsValue,
  gridDensitySelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid';

const DENSITY_OPTIONS = [
  { value: 'compact', icon: DensitySmallIcon },
  { value: 'standard', icon: DensityMediumIcon },
  { value: 'comfortable', icon: DensityLargeIcon },
];

/**
 * Kebab (⋮) at the end of a list toolbar, after the search, holding the
 * grid's own actions so they never compete with the filters for room:
 * - Columns / Filters open the native panels through apiRef (same as the
 *   native GridToolbarColumnsButton/GridToolbarFilterButton); Filters is
 *   hidden when the grid sets `disableColumnFilter`;
 * - density reads/writes the grid's own density state;
 * - column/cell borders aren't native grid state, so the parent owns and
 *   applies them (`showColumnVerticalBorder`/`sx` on the DataGrid).
 * Needs the grid context: render it inside `slots.toolbar`.
 */
export default function GridActionsMenu({
  showColumnBorder,
  onShowColumnBorderChange,
  showCellBorder,
  onShowCellBorderChange,
}) {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const { disableColumnFilter } = useGridRootProps();
  const density = useGridSelector(apiRef, gridDensitySelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);
  const label = t('dataGridSettings.menu');

  return (
    <>
      <Tooltip title={label}>
        <IconButton aria-label={label} onClick={(event) => setAnchorEl(event.currentTarget)}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            close();
            apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
          }}
        >
          <ListItemIcon>
            <ViewColumnOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('dataGridSettings.columns')}</ListItemText>
        </MenuItem>
        {!disableColumnFilter && (
          <MenuItem
            onClick={() => {
              close();
              apiRef.current.showFilterPanel();
            }}
          >
            <ListItemIcon>
              <FilterListIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('dataGridSettings.filters')}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <ListSubheader sx={{ lineHeight: 2.5 }}>{t('dataGridSettings.button')}</ListSubheader>
        {DENSITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === density}
            onClick={() => {
              apiRef.current.setDensity(option.value);
              close();
            }}
          >
            <ListItemIcon>
              <option.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t(`dataGridSettings.${option.value}Density`)}</ListItemText>
          </MenuItem>
        ))}
        {onShowColumnBorderChange && [
          <Divider key="borders-divider" />,
          <MenuItem key="column-borders" onClick={() => onShowColumnBorderChange(!showColumnBorder)}>
            <ListItemIcon>
              <Checkbox edge="start" checked={showColumnBorder} tabIndex={-1} disableRipple size="small" />
            </ListItemIcon>
            <ListItemText>{t('dataGridSettings.showColumnBorders')}</ListItemText>
          </MenuItem>,
          <MenuItem key="cell-borders" onClick={() => onShowCellBorderChange(!showCellBorder)}>
            <ListItemIcon>
              <Checkbox edge="start" checked={showCellBorder} tabIndex={-1} disableRipple size="small" />
            </ListItemIcon>
            <ListItemText>{t('dataGridSettings.showCellBorders')}</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}
